import { useState, useMemo } from 'react';
import { Category, Segment } from '@/data/mockData';

interface UseSegmentSelectionReturn {
  selectedSegments: string[];
  handleSegmentToggle: (segmentId: string) => void;
  handleCategoryToggle: (categoryId: string) => void;
  isSingleSegmentInCategory: boolean;
  selectedSegmentsByCategory: { category: Category; segments: Segment[] }[];
  selectedSegmentDetails: Segment[];
  singleSelectedSegment: Segment | null;
}

export const useSegmentSelection = (categories: Category[]): UseSegmentSelectionReturn => {
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);

  // Check if only one segment is selected in any category
  const isSingleSegmentInCategory = useMemo(() => {
    const categoriesWithSelection = new Set<string>();
    categories.forEach(category => {
      const hasSelectedSegment = category.segments.some(segment => 
        selectedSegments.includes(segment.id)
      );
      if (hasSelectedSegment) {
        categoriesWithSelection.add(category.id);
      }
    });
    
    // If only one category has selections
    if (categoriesWithSelection.size === 1) {
      const [categoryId] = Array.from(categoriesWithSelection);
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        const selectedInCategory = category.segments.filter(segment =>
          selectedSegments.includes(segment.id)
        );
        return selectedInCategory.length === 1;
      }
    }
    return false;
  }, [selectedSegments, categories]);

  const handleSegmentToggle = (segmentId: string) => {
    setSelectedSegments(prev =>
      prev.includes(segmentId)
        ? prev.filter(id => id !== segmentId)
        : [...prev, segmentId]
    );
  };

  const handleCategoryToggle = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return;

    const categorySegmentIds = category.segments.map(seg => seg.id);
    const allSelected = categorySegmentIds.every(id => selectedSegments.includes(id));

    if (allSelected) {
      setSelectedSegments(prev => prev.filter(id => !categorySegmentIds.includes(id)));
    } else {
      setSelectedSegments(prev => {
        const newSelection = [...prev];
        categorySegmentIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const selectedSegmentsByCategory = useMemo(() => {
    const result: { category: Category; segments: Segment[] }[] = [];
    categories.forEach(category => {
      const selectedInCategory = category.segments.filter(segment =>
        selectedSegments.includes(segment.id)
      );
      if (selectedInCategory.length > 0) {
        result.push({ category, segments: selectedInCategory });
      }
    });
    return result;
  }, [selectedSegments, categories]);

  const selectedSegmentDetails = useMemo(() => {
    const segments: Segment[] = [];
    selectedSegmentsByCategory.forEach(({ segments: categorySegments }) => {
      segments.push(...categorySegments);
    });
    return segments;
  }, [selectedSegmentsByCategory]);

  // Get the single selected segment if applicable
  const singleSelectedSegment = useMemo(() => {
    if (!isSingleSegmentInCategory) return null;
    
    for (const category of categories) {
      for (const segment of category.segments) {
        if (selectedSegments.includes(segment.id)) {
          return segment;
        }
      }
    }
    return null;
  }, [isSingleSegmentInCategory, selectedSegments, categories]);

  return {
    selectedSegments,
    handleSegmentToggle,
    handleCategoryToggle,
    isSingleSegmentInCategory,
    selectedSegmentsByCategory,
    selectedSegmentDetails,
    singleSelectedSegment,
  };
};

